import type { BrackenParams, LogParams, Palette } from './contracts';
import { line, n, path, randomAt, scaled } from './materials';

/** Broad bark planes and an exposed elliptical end; seed touches material marks only. */
export function buildFallenLog(p: LogParams, c: Palette, seed: number, detail: string): string {
  const split = p.profile === 'split';
  const top = p.profile === 'low' ? 33 : 27;
  const pieces = [
    path('M8 80Q34 72 73 74L198 73Q229 74 244 80Z', c.iron, 'opacity="0.25"'),
    path(`M23 35Q87 ${top - 4} 151 ${top}L220 ${top + 4}L239 39L235 62L222 75L27 78L14 63Z`, c.iron),
    path(`M24 36Q99 ${top} 153 ${top + 4}L219 ${top + 7}L233 41L226 65L215 72L27 74L19 61Z`, c.wood),
    path(`M27 36Q91 ${top - 4} 152 ${top}L217 ${top + 4}L229 39L218 47L141 41L70 45L28 48Z`, c.woodEdge, 'opacity="0.5"'),
    path('M33 61L101 58L151 60L223 55L226 65L215 72L29 74Z', c.shade, 'opacity="0.75"'),
    path('M91 30L91 19L102 10L100 24L114 29L112 35Z M170 31L183 18L188 20L181 32Z', c.wood),
    line('M96 28L98 20 M177 30L184 23', c.woodEdge, 2, 0.65),
    `<ellipse cx="25" cy="56" rx="19" ry="23" fill="${c.iron}"/>`,
    `<ellipse cx="25" cy="55" rx="15.5" ry="19.5" fill="${c.woodEdge}"/>`,
    `<ellipse cx="25" cy="55" rx="11.3" ry="15.7" fill="none" stroke="${c.wood}" stroke-width="1.5"/>`,
    line('M25 43C14 44 17 66 26 66C35 64 34 48 25 48C21 49 21 57 25 59', c.wood, 1.4, 0.95),
    line('M25 35L28 41L25 46 M11 61L17 58 M34 70L29 65', c.wood, 1.3, 0.8),
    line('M49 49Q93 43 118 47L145 45 M127 52L161 48L211 49 M51 66L86 63L115 65 M158 65L180 62L207 63', c.iron, 1.5, 0.7),
    line('M50 38L79 36 M117 35L145 35 M160 38L196 39', c.woodEdge, 1.7, 0.75),
  ];
  if (split) pieces.push(path('M215 32L231 29L225 39L242 35L232 47L239 53L228 59L232 67L218 72L221 56L215 49L223 41Z', c.woodEdge),
    line('M226 39L220 48L228 53L221 64', c.wood, 2));
  else pieces.push(path('M221 33L232 38L237 49L231 62L223 68L226 51Z', c.shade));
  if (p.mossCover > 0) {
    const moss = 'M43 32Q55 21 66 27L76 24L84 29L96 24L109 29L119 26L130 29L143 25L154 29L170 27L181 32L188 33L185 40L172 38L164 43L154 38L143 41L131 36L116 42L103 36L90 41L79 35L66 40L56 35L44 38Z';
    pieces.push(`<g transform="translate(43 0) scale(${n(p.mossCover / 0.7)} 1) translate(-43 0)">${path(moss, c.primary)}${line('M56 28L65 27 M96 28L105 29 M138 29L147 28', c.roofEdge, 1.8, 0.7)}</g>`);
  }
  if (detail === 'standard') for (let i = 0; i < 7; i++) {
    const x = 49 + randomAt(seed, 'bark-x', i) * 157, y = 43 + randomAt(seed, 'bark-y', i) * 17;
    pieces.push(line(`M${n(x)} ${n(y)}l${n(8 + randomAt(seed, 'bark-span', i) * 10)} -1.2`, c.woodEdge, 0.8, 0.3));
  }
  for (let i = 0; i < Math.round(p.saltWear * 12); i++) {
    const x = 51 + randomAt(seed, 'salt-x', i) * 157;
    pieces.push(line(`M${n(x)} ${n(top + 7)}l4 -0.6`, c.roofEdge, 1.3, 0.75));
  }
  // Two finite contact patches reach the exact frame baseline, without inventing collision.
  pieces.push(path('M31 76L64 75L76 80H22Z M188 75L215 74L231 80H180Z', c.shade));
  return scaled(pieces.join(''), p.width, p.height, 250, 80);
}

/** Dead fern skeletons: dry hooked rachises and sparse curled ribs, never green leaf masses. */
export function buildDeadBracken(p: BrackenParams, c: Palette, seed: number, detail: string): string {
  const pieces = [path('M9 98L21 94L33 95L43 93L61 96L69 98Z', c.iron, 'opacity="0.3"')];
  for (let i = 0; i < p.fronds; i++) {
    const t = i / (p.fronds - 1), side = t < 0.5 ? -1 : 1;
    const root = 34 + t * 8, tx = 13 + t * 46 + p.lean * 18;
    const broken = i < p.brokenTips, ty = 12 + Math.abs(t - 0.5) * 54 + randomAt(seed, 'frond-height', i) * 8 + (broken ? 12 : 0);
    const bow = tx + side * 7, color = i % 2 ? c.woodEdge : c.wood;
    pieces.push(line(`M${n(root)} 97C${n(root + side * 4)} 69 ${n(bow)} ${n(ty + 10)} ${n(tx)} ${n(ty)}`, c.iron, 2.6));
    pieces.push(line(`M${n(root)} 96C${n(root + side * 4)} 69 ${n(bow)} ${n(ty + 10)} ${n(tx)} ${n(ty)}`, color, 1.6));
    const ribs = detail === 'readable' ? 3 : 5;
    for (let j = 0; j < ribs; j++) {
      const v = 0.32 + j * 0.11, x = root + (tx - root) * v + side * 4 * Math.sin(v * Math.PI);
      const y = 97 + (ty - 97) * v, reach = 8 - j * 0.9;
      pieces.push(line(`M${n(x)} ${n(y)}q${n(-reach)} -3 ${n(-reach - 2)} -9m${n(reach + 2)} 9q${n(reach)} -5 ${n(reach - 1)} -11`, color, 1.1, 0.85));
    }
    if (!broken) pieces.push(line(`M${n(tx)} ${n(ty)}q${n(-side * 6)} -7 ${n(-side * 8)} 0`, color, 1.2));
    else pieces.push(line(`M${n(tx - 2)} ${n(ty + 1)}l4 -3`, c.roofEdge, 1));
  }
  pieces.push(line('M34 97L21 80L17 78 M40 97L56 81L61 80 M30 97L23 91', c.wood, 2));
  pieces.push(path('M23 98L29 94L35 95L39 91L44 95L50 94L55 98Z', c.wood));
  pieces.push(line('M31 96L35 97 M42 95L46 97', c.woodEdge, 1));
  return scaled(pieces.join(''), p.width, p.height, 76, 98);
}
